import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, LogIn, Users, ClipboardCheck, Package, Globe } from 'lucide-react';

const LOGO_URL = "/kaptan-hotels-logo.png";

export function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Giris yapilirken bir hata olustu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-b from-slate-800 to-slate-900">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
                    <img 
                        src="/kaptan-hotels-logo-light.png" 
                        alt="Kaptan Hotels" 
                        className="h-36 w-auto mb-8"
                    />
                    <h1 className="text-3xl font-bold mb-3 text-white text-center">
                        Otel Yönetim Sistemleri
                    </h1>
                    <p className="text-base text-white/70 mb-10 max-w-md text-center">
                        Bar stok takibi, maliyet raporlari ve IT envanter yonetimi
                    </p>
                    <div className="space-y-3 w-full max-w-md">
                        <div className="flex items-center gap-4 text-white/95 bg-white/10 rounded-xl p-5 hover:bg-white/15 transition-colors">
                            <div className="w-14 h-14 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                                <Users className="w-7 h-7" />
                            </div>
                            <span className="font-semibold text-lg">CRM & Guest Relations</span>
                        </div>
                        <div className="flex items-center gap-4 text-white/95 bg-white/10 rounded-xl p-5 hover:bg-white/15 transition-colors">
                            <div className="w-14 h-14 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                                <ClipboardCheck className="w-7 h-7" />
                            </div>
                            <span className="font-semibold text-lg">Operasyon Merkezi</span>
                        </div>
                        <div className="flex items-center gap-4 text-white/95 bg-white/10 rounded-xl p-5 hover:bg-white/15 transition-colors">
                            <div className="w-14 h-14 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                                <Package className="w-7 h-7" />
                            </div>
                            <span className="font-semibold text-lg">Stok & Cost Control</span>
                        </div>
                        <div className="flex items-center gap-4 text-white/95 bg-white/10 rounded-xl p-5 hover:bg-white/15 transition-colors">
                            <div className="w-14 h-14 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                                <Globe className="w-7 h-7" />
                            </div>
                            <span className="font-semibold text-lg">Online İşlemler</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
                <Card className="w-full max-w-md shadow-lg border-0">
                    <CardHeader className="text-center pb-2">
                        <div className="lg:hidden mb-6 flex justify-center">
                            <img src={LOGO_URL} alt="Kaptan Hotels" className="h-24" />
                        </div>
                        <CardTitle className="text-2xl text-slate-800">Hos Geldiniz</CardTitle>
                        <CardDescription className="text-slate-500">
                            Devam etmek icin giris yapin
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700">E-posta</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="ornek@grandkaptan.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                    className="h-11"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700">Sifre</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="********"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                    className="h-11"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-slate-800 hover:bg-slate-700 text-white font-medium"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <LogIn className="w-4 h-4 mr-2" />
                                )}
                                Giris Yap
                            </Button>

                            <p className="text-center text-sm text-slate-500 pt-2">
                                Hesabiniz yok mu?{' '}
                                <Link
                                    to="/register"
                                    className="text-slate-700 hover:underline font-medium"
                                    data-testid="register-link"
                                >
                                    Kayit Olun
                                </Link>
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default Login;
